import {ES_TIP_TYPES} from '@/consts/experienceScore/esTipTypes';
import experienceScoreStatuses from '@/consts/experienceScore/experienceScoreStatuses';
import type {TipData} from '@/types/experienceScore';
import type {InsightsAndTipsEntry, TipEntry} from '@/types/inference';

import metricMapping from './metricMapping';

interface TipContext {
    insightId: string;
    status: string;
    insightsAndTips?: Record<string, InsightsAndTipsEntry> | null;
}

const TIP_BY_STATUS: Record<string, 'optimalTip' | 'subOptimalTip'> = {
    [experienceScoreStatuses.OPTIMAL]: 'optimalTip',
    [experienceScoreStatuses.SUB_OPTIMAL]: 'subOptimalTip',
};

const getTipContent = (code: string, status: string) => {
    const tipField = TIP_BY_STATUS[status];

    if (!tipField) return undefined;

    return metricMapping.getMetric(code)?.[tipField];
};

/** Bucket membership in the inference response decides the copy, not the entry's own status. */
const getTipsToConsiderByStatus = ({insightId, status, insightsAndTips}: TipContext): TipData => {
    const tip = getTipContent(insightId, status);

    if (!tip?.description) return {};

    const {icon, title, description, button} = tip;
    const tipData: TipData = {
        icon,
        title,
        description,
        code: insightId,
        action: button?.action,
        tipsGroupType: button?.tipsGroupType,
        trackingValue: button?.trackingValue,
    };

    if (status !== experienceScoreStatuses.SUB_OPTIMAL) return {...tipData, type: ES_TIP_TYPES.SUCCESS};

    const entry = insightsAndTips?.[insightId] as TipEntry | undefined;
    const isButtonActive = Boolean(button?.text && entry?.require_engagement);

    return {
        ...tipData,
        type: ES_TIP_TYPES.WARNING,
        ...(isButtonActive && {isButtonActive, btnText: button?.text}),
    };
};

const getMetricPageTip = ({insightId, insightsAndTips}: Omit<TipContext, 'status'>): TipData | undefined => {
    const status = (insightsAndTips?.[insightId] as TipEntry | undefined)?.status;

    if (status !== experienceScoreStatuses.SUB_OPTIMAL) return undefined;

    const {type, icon, title, description} = getTipsToConsiderByStatus({insightId, status, insightsAndTips});

    return {type, icon, title, description};
};

export default {
    getTipsToConsiderByStatus,
    getMetricPageTip,
};
